import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import {
  Spinner, TreeStructure, Sparkle, Plus, Trash, PencilSimple, CaretDown,
  Check, X, Robot, UserCircle, FileText,
} from '@phosphor-icons/react';

const ISSUE_STATUS_META = {
  open: { label: 'Open', color: 'bg-white text-[#6B7280] border-[#E5E7EB]' },
  supported: { label: 'Supported', color: 'bg-green-50 text-green-700 border-green-200' },
  contradicted: { label: 'Contradicted', color: 'bg-red-50 text-red-700 border-red-200' },
  inconclusive: { label: 'Inconclusive', color: 'bg-amber-50 text-amber-700 border-amber-200' },
};
const ISSUE_STATUS_ORDER = ['open', 'supported', 'contradicted', 'inconclusive'];

const IssueStatusDropdown = ({ issue, onChange }) => {
  const [open, setOpen] = useState(false);
  const meta = ISSUE_STATUS_META[issue.status] || ISSUE_STATUS_META.open;
  return (
    <div className="relative">
      <button data-testid={`issue-status-btn-${issue._id}`} onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 text-[11px] font-ibm font-semibold px-2 py-0.5 rounded border ${meta.color}`}>
        {meta.label} <CaretDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 w-40" data-testid={`issue-status-menu-${issue._id}`}>
            {ISSUE_STATUS_ORDER.map(s => (
              <button key={s} data-testid={`issue-status-opt-${s}-${issue._id}`}
                onClick={() => { setOpen(false); if (s !== issue.status) onChange(issue._id, s); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-ibm hover:bg-[#F3F4F6] ${s === issue.status ? 'font-semibold text-[#002FA7]' : 'text-[#6B7280]'}`}>
                {ISSUE_STATUS_META[s].label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const AddIssueForm = ({ onAdd, onCancel, placeholder }) => {
  const [title, setTitle] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    onAdd(t);
    setTitle('');
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2" data-testid="issue-add-form">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder={placeholder} autoFocus
        data-testid="issue-add-title"
        className="flex-1 text-sm font-ibm border border-[#E5E7EB] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#002FA7]" />
      <button type="submit" disabled={!title.trim()} data-testid="issue-add-submit"
        className="flex items-center gap-1 px-3 py-1.5 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-xs hover:bg-[#00237D] disabled:opacity-50 transition-all">
        <Plus size={12} /> Add
      </button>
      <button type="button" onClick={onCancel} className="text-xs font-ibm text-[#6B7280] hover:underline">Cancel</button>
    </form>
  );
};

const IssueNode = ({ issue, children, depth, onStatus, onDelete, onSaveEdit, onAddChild }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(issue.title);
  const [addingChild, setAddingChild] = useState(false);

  const save = () => {
    const t = title.trim();
    if (t && t !== issue.title) onSaveEdit(issue._id, t);
    setEditing(false);
  };

  return (
    <div className={depth > 0 ? 'ml-6 border-l-2 border-[#E5E7EB] pl-4' : ''}>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`issue-${issue._id}`}>
        <div className="flex items-start gap-2">
          {issue.source === 'ai'
            ? <Robot size={16} className="text-[#002FA7] mt-0.5 shrink-0" weight="fill" title="AI-generated" />
            : <UserCircle size={16} className="text-[#6B7280] mt-0.5 shrink-0" weight="fill" title="Added by you" />}
          {editing ? (
            <div className="flex-1">
              <input value={title} onChange={e => setTitle(e.target.value)}
                data-testid={`issue-edit-input-${issue._id}`}
                className="w-full text-sm font-ibm font-semibold border border-[#E5E7EB] rounded-md px-2 py-1 focus:outline-none focus:border-[#002FA7]" />
              <div className="flex items-center gap-2 mt-1.5">
                <button onClick={save} data-testid={`issue-edit-save-${issue._id}`} className="flex items-center gap-1 text-xs font-ibm font-semibold text-[#002FA7] hover:underline"><Check size={12} /> Save</button>
                <button onClick={() => { setTitle(issue.title); setEditing(false); }} className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:underline"><X size={12} /> Cancel</button>
              </div>
            </div>
          ) : (
            <h4 className="text-sm font-ibm font-semibold text-[#09090B] leading-snug flex-1" data-testid={`issue-title-${issue._id}`}>{issue.title}</h4>
          )}
          <IssueStatusDropdown issue={issue} onChange={onStatus} />
        </div>

        {issue.description && <p className="text-xs text-[#6B7280] font-ibm pl-6 mt-1.5 leading-relaxed">{issue.description}</p>}
        {issue.status_rationale && (
          <p className="text-[11px] text-[#94A3B8] font-ibm italic pl-6 mt-1" data-testid={`issue-rationale-${issue._id}`}>Why: {issue.status_rationale}</p>
        )}
        {(issue.supporting_evidence || []).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pl-6 mt-2">
            {issue.supporting_evidence.map((ev, i) => (
              <span key={`${issue._id}-ev-${i}`} className="flex items-center gap-1 text-[10px] font-ibm px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">
                <FileText size={10} /> {ev}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mt-3 pl-6">
          {depth === 0 && (
            <button onClick={() => setAddingChild(a => !a)} data-testid={`issue-add-child-${issue._id}`}
              className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-[#002FA7]"><Plus size={12} /> Sub-issue</button>
          )}
          {!editing && (
            <button onClick={() => setEditing(true)} data-testid={`issue-edit-${issue._id}`}
              className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-[#002FA7]"><PencilSimple size={12} /> Edit</button>
          )}
          <button onClick={() => onDelete(issue._id)} data-testid={`issue-delete-${issue._id}`}
            className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-red-600"><Trash size={12} /> Delete</button>
        </div>

        {addingChild && (
          <div className="pl-6 mt-3">
            <AddIssueForm placeholder="New sub-issue title…" onCancel={() => setAddingChild(false)}
              onAdd={(t) => { onAddChild(t, issue._id); setAddingChild(false); }} />
          </div>
        )}
      </div>
      {children && <div className="space-y-2 mt-2">{children}</div>}
    </div>
  );
};

export const IssuesTab = ({ caseId }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/issues`);
      setIssues(data.issues || []);
    } catch (err) {
      logger.error('failed to load issues:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/cases/${caseId}/issues/generate`);
      await load();
    } catch (err) {
      logger.error('failed to generate issue tree:', err);
      alert('Failed to generate the issue tree. Make sure documents have been analyzed first.');
    } finally {
      setGenerating(false);
    }
  };

  const addIssue = async (title, parentId = null) => {
    try {
      const { data } = await api.post(`/cases/${caseId}/issues`, { title, parent_id: parentId });
      setIssues(prev => [...prev, data.issue]);
      setShowAdd(false);
    } catch (err) {
      logger.error('failed to add issue:', err);
      alert('Failed to add issue.');
    }
  };

  const setStatus = async (iid, status) => {
    setIssues(prev => prev.map(i => (i._id === iid ? { ...i, status } : i)));
    try {
      await api.patch(`/cases/${caseId}/issues/${iid}`, { status });
    } catch (err) {
      logger.error('failed to update issue status:', err);
      load();
    }
  };

  const saveEdit = async (iid, title) => {
    setIssues(prev => prev.map(i => (i._id === iid ? { ...i, title } : i)));
    try {
      await api.patch(`/cases/${caseId}/issues/${iid}`, { title });
    } catch (err) {
      logger.error('failed to edit issue:', err);
      load();
    }
  };

  const remove = async (iid) => {
    const prev = issues;
    setIssues(p => p.filter(i => i._id !== iid && i.parent_id !== iid));
    try {
      await api.delete(`/cases/${caseId}/issues/${iid}`);
    } catch (err) {
      logger.error('failed to delete issue:', err);
      setIssues(prev);
    }
  };

  const roots = issues.filter(i => !i.parent_id);
  const childrenOf = (id) => issues.filter(i => i.parent_id === id);

  const statusCounts = issues.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="issues-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  return (
    <div data-testid="issues-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <TreeStructure size={20} className="text-[#002FA7]" weight="fill" /> Issue Tree
          </h3>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5 max-w-2xl">
            The case broken into its distinct issues and sub-issues — each tracked through an evidence-based lifecycle: <span className="font-semibold text-green-700">Supported</span>, <span className="font-semibold text-red-700">Contradicted</span>, or <span className="font-semibold text-amber-700">Inconclusive</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowAdd(s => !s)} data-testid="issue-toggle-add"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md text-sm hover:bg-[#F3F4F6] transition-all">
            <Plus size={14} /> Add issue
          </button>
          <button onClick={generate} disabled={generating} data-testid="issue-generate-button"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all">
            {generating ? <><Spinner size={14} className="animate-spin" /> Generating…</> : <><Sparkle size={14} weight="fill" /> Generate Issue Tree</>}
          </button>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="issue-status-counts">
          {ISSUE_STATUS_ORDER.filter(s => statusCounts[s]).map(s => (
            <span key={s} className={`text-[11px] font-ibm font-semibold px-2 py-0.5 rounded border ${ISSUE_STATUS_META[s].color}`}>
              {ISSUE_STATUS_META[s].label}: {statusCounts[s]}
            </span>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <AddIssueForm placeholder="New issue title…" onAdd={(t) => addIssue(t)} onCancel={() => setShowAdd(false)} />
        </div>
      )}

      {roots.length === 0 ? (
        <div className="text-center py-14 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="issues-empty">
          <TreeStructure size={40} className="mx-auto text-[#002FA7]/40 mb-3" weight="fill" />
          <p className="text-[#6B7280] font-ibm">No issues yet. Click <span className="font-semibold text-[#002FA7]">Generate Issue Tree</span> to have the AI decompose your case into issues with evidence-based statuses, or add your own.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {roots.map(root => (
            <IssueNode key={root._id} issue={root} depth={0}
              onStatus={setStatus} onDelete={remove} onSaveEdit={saveEdit} onAddChild={(t, pid) => addIssue(t, pid)}
              children={childrenOf(root._id).map(child => (
                <IssueNode key={child._id} issue={child} depth={1}
                  onStatus={setStatus} onDelete={remove} onSaveEdit={saveEdit} onAddChild={() => {}} children={null} />
              ))}
            />
          ))}
        </div>
      )}
    </div>
  );
};
